// src/pages/Home.js
import React, { useState, useEffect, useRef } from 'react';
import { Github, Linkedin, Mail, ExternalLink, ChevronDown, Code, Sparkles, Heart, Zap, Trophy, Star, ArrowRight, GraduationCap, X, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Home() {
    // Light mode state with localStorage persistence
    const isLightMode = (() => {
      if (typeof window !== 'undefined') {
        return localStorage.getItem('theme') === 'light';
      }
      return false;
    })();

    // Apply theme on mount and when it changes
    useEffect(() => {
      if (isLightMode) {
        document.documentElement.classList.add('light-mode');
        localStorage.setItem('theme', 'light');
      } else {
        document.documentElement.classList.remove('light-mode');
        localStorage.setItem('theme', 'dark');
      }
    }, [isLightMode]);

    // Active tab for projects
    const [activeTab, setActiveTab] = useState('Technical Solutions');

    // Scroll to top on page load
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  // Disable browser zoom (pinch/trackpad, Ctrl/Cmd zoom, gesture) and set viewport
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport ? viewport.getAttribute('content') : null;
    let createdMeta = null;
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    } else {
      createdMeta = document.createElement('meta');
      createdMeta.name = 'viewport';
      createdMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
      document.head.appendChild(createdMeta);
    }

    const wheelHandler = (e) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };
    const keyHandler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
        e.preventDefault();
      }
    };
    const gestureHandler = (e) => e.preventDefault();

    window.addEventListener('wheel', wheelHandler, { passive: false });
    window.addEventListener('keydown', keyHandler, { passive: false });
    window.addEventListener('gesturestart', gestureHandler);
    window.addEventListener('gesturechange', gestureHandler);

    return () => {
      if (viewport) {
        if (originalContent !== null) viewport.setAttribute('content', originalContent);
      } else if (createdMeta) {
        createdMeta.remove();
      }
      window.removeEventListener('wheel', wheelHandler);
      window.removeEventListener('keydown', keyHandler);
      window.removeEventListener('gesturestart', gestureHandler);
      window.removeEventListener('gesturechange', gestureHandler);
    };
  }, []);
  const [visibleSections, setVisibleSections] = useState({});
  const [heroTextVisible, setHeroTextVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [nameLoaded, setNameLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [ripples, setRipples] = useState([]);
  const particlesRef = useRef([]);
  const rippleIdRef = useRef(0);
  
  const tagline = "Aspiring IT SecOps Engineer and Project Manager";

  // Generate stable particle positions on mount (increased to 24 dots)
  useEffect(() => {
    // Create a more scattered and balanced grid of particles
    const rows = 4;
    const cols = 6;
    const total = rows * cols;
    const xSpacing = 100 / cols;
    const ySpacing = 100 / rows;
    particlesRef.current = Array.from({ length: total }).map((_, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      // Edge-to-edge scatter, no margins
      const baseX = col * xSpacing + Math.random() * xSpacing;
      const baseY = row * ySpacing + Math.random() * ySpacing;
      return {
        id: i,
        baseX,
        baseY,
        color: ['#ec4899', '#a855f7', '#3b82f6'][i % 3],
        delay: i * 0.15,
        duration: 7 + (i % 3) * 2
      };
    });
  }, []);

  // Typing effect for tagline
  useEffect(() => {
    setHeroTextVisible(true);
    let index = 0;
    const timer = setInterval(() => {
      if (index <= tagline.length) {
        setTypedText(tagline.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 80);
    return () => clearInterval(timer);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Cursor tracking with ripple creation (no smoothing)
  useEffect(() => {
    let lastRippleTime = 0;
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      // Create ripple every 600ms while moving
      const now = Date.now();
      if (now - lastRippleTime > 600) {
        lastRippleTime = now;
        const id = rippleIdRef.current++;
        setRipples(prev => [...prev.slice(-3), { id, x: e.clientX, y: e.clientY, startTime: now }]);
        setTimeout(() => {
          setRipples(prev => prev.filter(r => r.id !== id));
        }, 2000);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Wait until the main name node is present/has text, then reveal page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkName = () => {
      const nameEl = document.querySelector('#hero h1 .shimmer-text');
      return nameEl && nameEl.textContent && nameEl.textContent.trim().length > 0;
    };
    if (checkName()) {
      setNameLoaded(true);
      return;
    }
    const hero = document.getElementById('hero') || document.body;
    const observer = new MutationObserver(() => {
      if (checkName()) {
        setNameLoaded(true);
        observer.disconnect();
      }
    });
    observer.observe(hero, { childList: true, subtree: true, characterData: true });
    const timeout = setTimeout(() => {
      if (!nameLoaded) setNameLoaded(true);
      observer.disconnect();
    }, 3000);
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [nameLoaded]);

  // Tech stack with logo URLs (ordered and expanded)
  const techStack = [
    { name: 'Netskope', icon: '/images/netskope.svg' },
    { name: 'CrowdStrike', icon: 'https://www.google.com/s2/favicons?domain=crowdstrike.com&sz=128' },
    { name: 'Microsoft 365 Admin', icon: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' },
    { name: 'Microsoft Entra', icon: 'https://cdn.worldvectorlogo.com/logos/microsoft-entra-id-1.svg' },
    { name: 'Microsoft Defender', icon: 'https://commons.wikimedia.org/wiki/Special:FilePath/Windows-defender.svg' },
    { name: 'Microsoft Exchange', icon: 'https://commons.wikimedia.org/wiki/Special:FilePath/Microsoft_Exchange_(2019-present).svg' },
    { name: 'Microsoft Intune', icon: 'https://commons.wikimedia.org/wiki/Special:FilePath/Microsoft-intune.svg' },
    { name: 'Microsoft Azure', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg' },
    { name: 'ManageEngine', icon: 'https://www.google.com/s2/favicons?domain=manageengine.com&sz=128' },
    { name: 'KnowBe4', icon: 'https://cdn.jsdelivr.net/gh/glincker/thesvg@main/public/icons/knowbe4/default.svg' },
    { name: 'ClickUp', icon: 'https://svgl.app/library/clickup.svg' },
    { name: 'Zapier', icon: 'https://www.google.com/s2/favicons?domain=zapier.com&sz=128' },
    { name: 'Microsoft 365', icon: '/images/microsoft365.svg' },
    { name: 'Google Workspace', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg' },
    { name: 'Meta Business Suite', icon: 'https://cdn.simpleicons.org/meta' },
    { name: 'Canva', icon: '/images/canva.svg' },
    { name: 'Adobe Creative Cloud', icon: 'https://www.adobe.com/content/dam/shared/images/product-icons/svg/creative-cloud.svg' },
    { name: 'Wix', icon: 'https://cdn.simpleicons.org/wix' },
    { name: 'WordPress', icon: 'https://cdn.simpleicons.org/wordpress' },
    { name: 'Notion', icon: 'https://cdn.simpleicons.org/notion', invert: true },
    { name: 'Figma', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
    { name: 'Google Apps Script', icon: '/images/appsscript.svg' },
    { name: 'QuickBooks', icon: 'https://cdn.simpleicons.org/quickbooks' },
    { name: 'JavaScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
    { name: 'TypeScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
    { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
    { name: 'Node.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
    { name: 'Express', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg', invert: true },
    { name: 'EJS', icon: 'https://cdn.simpleicons.org/ejs' },
    { name: 'MongoDB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
    { name: 'ASP.NET', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dotnetcore/dotnetcore-original.svg' },
    { name: 'Java', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
    { name: 'Python', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
    { name: 'C#', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg' },
    { name: 'C++', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg' },
    { name: 'PHP', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
    { name: 'Dart', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dart/dart-original.svg' },
    { name: 'Flutter', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg' },
    { name: 'MySQL', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
    { name: 'Firebase', icon: 'https://cdn.simpleicons.org/firebase' },
    { name: 'Sass', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sass/sass-original.svg' },
    { name: 'CSS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
    { name: 'HTML', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
    { name: 'Visual Studio Code', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
    { name: 'Visual Studio', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/visualstudio/visualstudio-plain.svg' },
    { name: 'Android Studio', icon: '/images/androidstudio.svg' },
    { name: 'Apache NetBeans', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/netbeans/netbeans-original.svg' },
    { name: 'GitHub', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg', invert: true },
  ];

  const projects = [
    {
      title: 'SCO Creative Optimization for Requests and Engagement System (S-CORE)',
      description: 'A centralized web-based system designed to automate service requests, approval workflows, and task monitoring with performance analytics.',
      tech: ['Node.js', 'Express', 'MongoDB', 'EJS', 'JavaScript', 'DigitalOcean'],
      type: 'Institutional Office Website',
      year: '2025',
      image: '/images/project1.png',
      link: 'https://dlsuds-core.me',
      github: 'https://github.com/caryljoycabrera/S-CORE.git',
      category: 'Technical Solutions'
    },
    {
      title: 'CareSync: A Mobile Solution for Elderly Medication Adherence',
      description: 'A mobile application that assists elderly individuals and their caregiversin managing their medication schedules through notification reminders, alarms, and tracking.',
      tech: ['Flutter', 'Dart', 'Firebase'],
      type: 'Elderly Care Mobile App',
      year: '2025',
      image: '/images/project2.png',
      link: 'https://www.canva.com/design/DAG7Rbsj_4A/T5gF6Goj8S9RJR_8D67L3w/view?utm_content=DAG7Rbsj_4A&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utm_id=h9a52723737',
      github: 'https://github.com/carebear1919/caresync_mobileapplication.git',
      category: 'Technical Solutions'
    },
    {
      title: 'Caryl\'s Portfolio',
      description: 'A modern portfolio featuring custom animations, responsive design, and a showcase of skills, projects, and achievements.',
      tech: ['React', 'Tailwind CSS', 'JavaScript', 'Vercel'],
      type: 'Personal Portfolio Website',
      year: '2025',
      image: '/images/project3.png',
      link: null,
      github: 'https://github.com/caryljoycabrera/devportfolio.git',
      category: 'Technical Solutions'
    },
    {
      title: 'Foodpanda x Wendy\'s Mega Collaboration',
      description: 'A themed kiosk ordering system for multiple food brands, featuring menu browsing, cart management, and order checkout functionalities.',
      tech: ['Node.js', 'Express', 'EJS', 'JavaScript'],
      type: 'Online Ordering Website',
      year: '2025',
      image: '/images/project4.png',
      link: null,
      github: 'https://github.com/caryljoycabrera/Foodpanda-Wendys-Collab.git',
      category: 'Technical Solutions'
    },
    {
      title: 'PBB Celebrity Edition: The Big Night',
      description: 'An event registration system for reality TV show awarding event, allowing participants to register and manage their attendance seamlessly.',
      tech: ['Google Apps Script', 'Google Sheets', 'JavaScript', 'GitHub Pages'],
      type: 'Event Registration System',
      year: '2025',
      image: '/images/project5.png',
      link: 'https://caryljoycabrera.github.io/PBB-Big-Night/',
      github: 'https://github.com/caryljoycabrera/PBB-Big-Night.git',
      category: 'Technical Solutions'
    },
    {
      title: 'Purrfect Care',
      description: 'A comprehensive pet shelter management system facilitating animal intake, adoption processes, donation records, and announcements.',
      tech: ['ASP.NET', 'C#', 'MySQL', 'MVC Architecture'],
      type: 'Pet Shelter Services Website',
      year: '2024 ',
      image: '/images/project6.png',
      link: null,
      github: null,
      category: 'Technical Solutions'
    },
    {
      title: 'Chasing Victory Golden: The National Esports Tournament and Gaming Convention',
      description: 'A large-scale esports and cosplay event that united gaming enthusiasts through competitive tournaments, cosplay showcases, and interactive booth experiences supported by notable brands and guests, featuring exclusive appearances by top cosplay icon Charess and international Valorant champion PatMen.',
      tech: [],
      type: 'Esports and Cosplay Convention',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl16.png',
      link: 'https://www.facebook.com/media/set/?set=a.122195282144448213&type=3',
      github: null
    },
    {
      title: 'Feast of Saint Julian Alfredo',
      description: 'A spiritual and community-building celebration organized that engaged CICS and non-CICS participants through a solemn mass, fellowship gathering, and creative activities to honor the college\'s patron saint.',
      tech: [],
      type: 'Collegiate Event',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl15.png',
      link: 'https://www.facebook.com/DLSUD.CICSSG/posts/pfbid08aAZB11LePM3t4XWKWwJBdQc23CVS2oHpBCPmEF5crNPxKhhHoG3QKMYCnjNXUEwl',
      github: null
    },
    {
      title: 'SpeciFi: Find Your Specialization',
      description: 'A strategic academic guidance workshop designed to assist 500 underclassmen in selecting their specialization tracks through comprehensive curriculum overviews and career insights provided by faculty and alumni.',
      tech: [],
      type: 'Specialization Seminar',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl14.png',
      link: 'https://www.facebook.com/DLSUD.CICSSG/posts/pfbid0263vzhAsrHgVMDAU5BeFmKvWdkkKvAFbLMnQgCoG2voJ1p77AQ3HyRXLizePvVJkFl',
      github: null
    },
    {
      title: 'CICS College Orientation',
      description: 'An orientation program for incoming students of the College of Information and Computer Studies, featuring interactive activities, informative sessions, and community-building segments to welcome and integrate new students into the college community.',
      tech: [],
      type: 'Collegiate Event',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl13.png',
      link: 'https://www.facebook.com/DLSUD.CICSSG/posts/pfbid024TRxtkFqoBB3j8DjphmqBvQjANQduMWdCsxetPgzgH3vwvKN2ym8n5aqbwfEWV4Cl',
      github: null
    },
    {
      title: 'Who Can See Me?: Protecting Privacy and Security in the Digital Landscape',
      description: 'A virtual seminar on digital privacy and security that engaged participants in expert-led discussions regarding access rights and digital footprints, empowering them to navigate the online world safely and responsibly.',
      tech: [],
      type: 'Webinar Event',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl12.png',
      link: 'https://www.facebook.com/WhoCanSeeMeWebinar',
      github: null
    },
    {
      title: 'CICS Research Week 2025',
      description: 'An academic event that showcased research projects and innovations from students of the College of Information and Computer Studies, fostering a culture of inquiry and knowledge sharing within the academic community through exhibit, turnover ceremony, ethics webinar, and awarding ceremony.',
      tech: [],
      type: 'Collegiate Event',
      year: 2025,
      category: 'Community Leadership',
      image: '/images/cl11.png',
      link: 'https://www.facebook.com/media/set/?vanity=dlsud.cics&set=a.122104882700863997',
      github: null
    },
    {
      title: 'C Program Process Diagram',
      description: 'A presentation that illustrates the step-by-step process of compiling and executing a C program, from writing the source code to terminating the executable file.',
      tech: [],
      type: 'Presentation',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co19.png',
      link: '/files/CProgramProcessDiagram.pdf',
      github: null
    },
    {
      title: 'Computer Organization',
      description: 'A presentation that provides an overview of computer organization, covering topics such as computer hardware, compiler, and system architecture.',
      tech: [],
      type: 'Presentation',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co18.png',
      link: '/files/ComputerOrganization.pdf',
      github: null
    },
    {
      title: 'Booting Process of a Computer',
      description: 'A presentation that explains the step-by-step booting process of a computer, from power-on, BIOS initialization, POST (Power-On Self-Test), to loading the operating system.',
      tech: [],
      type: 'Presentation',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co17.png',
      link: '/files/BootingProcess.pdf',
      github: null
    },
    {
      title: 'Disciplines of Social Sciences Infographic',
      description: 'An infographic that illustrates the various disciplines within the social sciences field, highlighting their key areas of study and contributions to understanding human behavior and society.',
      tech: [],
      type: 'Graphic Design',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co11.png',
      link: null,
      github: null
    },
    {
      title: 'Parola Zine Cover Design',
      description: 'A sample cover design for Parola, a zine publication from a non-profit organization that showcases creative works and stories.',
      tech: [],
      type: 'Graphic Design',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co10.png',
      link: null,
      github: null
    },
    {
      title: 'Evolution of Media Infographic',
      description: 'An infographic that illustrates the evolution of media from traditional forms to digital platforms.',
      tech: [],
      type: 'Graphic Design',
      year: null,
      category: 'Creative Outputs',
      image: '/images/co9.png',
      link: null,
      github: null
    }
  ];

  const awards = [
    {
      title: 'Summa Cum Laude',
      event: 'Graduation',
      year: '2026',
      description: ''
    },
    {
      title: 'Golden Pin Award',
      event: 'Graduation',
      year: '2026',
      description: ''
    },
    {
      title: 'Pinakamahusay na Opisyal ng Taon para sa Pamahalaang Pangmag-aaral ng Kolehiyo (Best Officer of the Year for College Student Government',
      event: 'Luntiang Parangal (Green Honors)',
      year: '2026',
      description: ''
    },
    {
      title: 'Opisyal ng Taon para sa Pamahalaang Pangmag-aaral ng CICS (Officer of the Year for CICS Student Government',
      event: 'Luntiang Parangal (Green Honors)',
      year: '2026',
      description: ''
    },
    {
      title: 'Excellence in Leadership',
      event: 'Pioneer 2026: The CICS Code of Excellence Awards',
      year: '2026',
      description: 'Honored for exemplary leadership in college student government.'
    },
    {
      title: 'Lider ng Taon (Leader of the Year)',
      event: 'Luntiang Parangal (Green Honors)',
      year: '2025',
      description: 'Received for exceptional leadership in the university among all student leaders. Luntiang Parangal is an annual recognition event that honors students, athletes, writers, artists, and student organizations for their outstanding leadership, teamwork, and solidarity.'
    },
    {
      title: 'Opisyal ng Taon (Officer of the Year)',
      event: 'Luntiang Parangal (Green Honors)',
      year: '2025',
      description: 'Awarded for outstanding performance as student officer. Luntiang Parangal celebrates those who have made significant contributions to the university and provided exceptional service to the community.'
    },
    {
      title: 'Best Project Head',
      event: 'Luminaries: CSO-RSO Night of Stars and Honors',
      year: '2025',
      description: 'Recognized for successful institutional events and initiatives at the Council of Student Organizations.'
    },
    {
      title: 'Outstanding Monitoring Executive Board',
      event: 'Luminaries: CSO-RSO Night of Stars and Honors',
      year: '2025',
      description: 'Excelled in oversight and support of student organizations at the Council of Student Organizations.'
    },
    {
      title: 'Excellence in Leadership',
      event: 'Gawad Agham (Science Awards)',
      year: '2024',
      description: 'Honored for exemplary leadership in college student government.'
    }
  ];

  const education = {
    degree: 'Bachelor of Science in Information Technology',
    specialization: 'Web Development',
    university: 'De La Salle University – Dasmariñas',
    graduation: 'August 2026',
    gpa: '3.88/4.00',
    honors: ['Summa Cum Laude', 'Academic Scholarship', 'Municipality Scholarship', 'District Scholarship', 'Student Financial Aid Grant', 'DOLE Special Program for Employment of Students (SPES) Beneficiary'],
    achievement: 'Developed an online request approval and management system adopted by Strategic Communications Office'
  };

  const certifications = [
    {
      title: 'Networking Devices and Initial Configuration',
      org: 'Cisco Networking Academy',
      year: '2026',
      icon: '💻'
    },
    {
      title: 'Networking Basics',
      org: 'Cisco Networking Academy',
      year: '2026',
      icon: '🛜'
    },
    {
      title: 'Global English (CEFR C1 Equivalent)',
      org: 'TOEIC',
      year: '2026',
      icon: '🌐'
    },
    {
      title: 'Endpoint Security',
      org: 'Cisco Networking Academy',
      year: '2025',
      icon: '🛡️'
    },
    {
      title: 'Cyber Threat Management',
      org: 'Cisco Networking Academy',
      year: '2025',
      icon: '⚔️'
    },
    {
      title: 'IT Specialist – Databases',
      org: 'Certiport',
      year: '2024',
      icon: '🗄️'
    },
    {
      title: 'Introduction to Cybersecurity',
      org: 'Cisco Networking Academy',
      year: '2024',
      icon: '🔐'
    },
    {
      title: 'PCAP: Programming Essentials in Python',
      org: 'OpenEDG',
      year: '2023',
      icon: '🐍'
    }
  ];

  const workExperience = [
    {
      role: 'IT Security and Operations Analyst',
      company: 'Anchored Solutions MNL Inc',
      type: 'Full-Time | Remote',
      period: 'June 2026 - Present',
      description: 'Assisting in security monitoring and triage, incident response activities, managing endpoint and user devices, identity and access management, IT service desk and help desk, change management, and IT projects'
    },
    {
      role: 'IT Compliance Intern',
      company: 'Anchored Solutions MNL Inc',
      type: 'Internship | Full-Time | Remote',
      period: 'Feb 2026 - May 2026',
      description: 'Supported the team in ensuring organizational adherence to information security standards through the coordination of ISO27001 initiatives, risk assessments, and vulnerability monitoring campaigns'
    },
    {
      role: 'Virtual Assistant',
      company: 'Freelance (Local & International Clients)',
      type: 'Part-Time | Hourly & Project-Based | Remote',
      period: '2022 - 2025',
      description: 'Delivered project management, content creation, and tutoring services, driving client performance and revenue growth through community engagement strategies'
    },
    {
      role: 'Student Assistant',
      company: 'Scholarship Duty',
      type: 'Fixed Hours | On-Site',
      period: '2022 - 2024',
      description: 'Provided administrative and technical support, enhancing office operations through efficient data management and communication'
    }
  ];

  const values = [
    { word: 'Strategic', description: 'Every decision serves a purpose' },
    { word: 'Persevering', description: 'Difficulties are opportunities' },
    { word: 'Genuine', description: 'True to my voice and values' },
  ];

  // Modal for full-screen image view
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalTopOffset, setModalTopOffset] = useState(0);
  const closeBtnRef = useRef(null);
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const savedScrollYRef = useRef(0);

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      const h = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      // add a small gap
      setModalTopOffset(h + 8);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Close on Escape and focus close button when modal opens
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    if (isModalOpen) {
      document.addEventListener('keydown', onKey);
      setTimeout(() => closeBtnRef.current?.focus(), 0);
      // prevent body scroll
      const scrollY = window.scrollY;
      savedScrollYRef.current = scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.touchAction = 'none';
      // reset zoom and pan
      setZoom(1);
      setPanX(0);
      setPanY(0);
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
      window.scrollTo(0, savedScrollYRef.current);
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
      window.scrollTo(0, savedScrollYRef.current);
    };
  }, [isModalOpen]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 1));
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      constrainPan(newPanX, newPanY);
    }
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (zoom > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - panX, y: touch.clientY - panY });
    }
  };
  const handleTouchMove = (e) => {
    if (isDragging && zoom > 1 && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      const newPanX = touch.clientX - dragStart.x;
      const newPanY = touch.clientY - dragStart.y;
      constrainPan(newPanX, newPanY);
    }
  };
  const handleTouchEnd = () => setIsDragging(false);

  const constrainPan = (newPanX, newPanY) => {
    const img = imgRef.current;
    if (!img || !img.complete || img.naturalWidth === 0) return;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const containerWidth = window.innerWidth - 32; // approximate padding
    const containerHeight = window.innerHeight - modalTopOffset - 128;
    const scaleX = containerWidth / naturalWidth;
    const scaleY = containerHeight / naturalHeight;
    const scale = Math.min(scaleX, scaleY);
    const displayedWidth = naturalWidth * scale * zoom;
    const displayedHeight = naturalHeight * scale * zoom;
    const maxPanX = Math.min((displayedWidth - containerWidth) / 2, containerWidth / 2);
    const maxPanY = Math.min((displayedHeight - containerHeight) / 2, containerHeight / 2);
    setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
    setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
  };

  // Animation helper
  const sectionClass = (id) => 
    `transition-all duration-1000 ${visibleSections[id] ? 'opacity-100 translate-y-0' : 'opacity-0 sm:translate-y-8'}`;

  return (
    <>
      <div className={`min-h-screen relative overflow-x-hidden w-full transition-colors duration-300 ${isLightMode ? 'bg-slate-50 text-slate-900' : 'bg-slate-950 text-slate-100'}`} style={{ userSelect: 'none' }}>
      {/* Custom CSS for animations and full-viewport background */}
      <style>{`
        html, body {
          min-width: 100vw;
          min-height: 100vh;
          background: #0f172a !important; /* Tailwind's bg-slate-950 */
          overflow-x: hidden;
          transition: background 0.3s ease;
        }
        html.light-mode, html.light-mode body {
          background: #f8fafc !important; /* Tailwind's slate-50 */
        }
        #root {
          min-height: 100vh;
        }
        html {
          scroll-behavior: smooth;
        }
        /* Arsenica Antiqua font for section titles */
        @font-face {
          font-family: 'Arsenica Antiqua';
          src: url('/fonts/ArsenicaAntiqua.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        /* Only apply to main section titles, not all h2/h3 */
        .max-w-5xl > h1,
        .max-w-5xl > h2,
        .max-w-5xl > h3 {
          font-family: 'Arsenica Antiqua', serif !important;
        }
        /* Prevent text selection globally */
        html, body, #root, .unselectable, .bg-slate-950 {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        *::selection {
          background: transparent !important;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes glow {
          0%, 100% { opacity: 0.4; filter: blur(20px); }
          50% { opacity: 0.8; filter: blur(30px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes ripple {
          0% { 
            transform: translate(-50%, -50%) scale(0);
            opacity: 1;
          }
          100% { 
            transform: translate(-50%, -50%) scale(1);
            opacity: 0;
          }
        }
        @keyframes textReveal {
          from { clip-path: inset(0 100% 0 0); }
          to { clip-path: inset(0 0% 0 0); }
        }
        @keyframes borderGlow {
          0%, 100% { border-color: rgba(236, 72, 153, 0.3); }
          50% { border-color: rgba(168, 85, 247, 0.5); }
        }
        .float { animation: float 6s ease-in-out infinite; }
        .glow { animation: glow 4s ease-in-out infinite; }
        .slide-up { animation: slideUp 0.8s ease-out forwards; }
        .fade-in { animation: fadeIn 1s ease-out forwards; }
        .shimmer-text {
          background: linear-gradient(90deg, #ec4899, #a855f7, #3b82f6, #a855f7, #ec4899);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 12s linear infinite;
        }
        .static-shimmer-text {
          background: linear-gradient(90deg, #ec4899, #a855f7, #3b82f6, #a855f7, #ec4899);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .border-glow { animation: borderGlow 3s ease-in-out infinite; }
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }
        .cursor-blink::after {
          content: '|';
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .photo-placeholder {
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
          border: 2px dashed rgba(236, 72, 153, 0.3);
        }
        .tech-icon {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .tech-icon:hover {
          transform: translateY(-8px) scale(1.1);
          filter: drop-shadow(0 10px 20px rgba(236, 72, 153, 0.3));
        }
        .arsenica-antiqua {
          font-family: 'Arsenica Antiqua', serif !important;
        }
        
        /* Light mode styles */
        html.light-mode, html.light-mode body {
          background: #f8fafc !important; /* Tailwind's slate-50 */
        }
        html.light-mode .bg-slate-950 {
          background: #f8fafc !important;
          color: #1e293b !important;
        }
        html.light-mode .text-slate-100 {
          color: #0f172a !important;
        }
        html.light-mode .text-slate-200 {
          color: #1e293b !important;
        }
        html.light-mode .text-slate-300 {
          color: #334155 !important;
        }
        html.light-mode .text-slate-400 {
          color: #475569 !important;
        }
        html.light-mode .text-slate-500 {
          color: #64748b !important;
        }
        html.light-mode .text-slate-600 {
          color: #475569 !important;
        }
        html.light-mode .text-slate-700 {
          color: #334155 !important;
        }
        html.light-mode .bg-slate-900\\/50,
        html.light-mode .bg-slate-900\\/80 {
          background: rgba(241, 245, 249, 0.9) !important;
          border-color: #cbd5e1 !important;
        }
        html.light-mode .bg-slate-950\\/80 {
          background: rgba(241, 245, 249, 0.95) !important;
        }
        html.light-mode .bg-slate-900\\/40 {
          background: rgba(241, 245, 249, 0.7) !important;
        }
        html.light-mode .border-slate-800 {
          border-color: #cbd5e1 !important;
        }
        html.light-mode .border-slate-900 {
          border-color: #e2e8f0 !important;
        }
        html.light-mode .border-slate-700 {
          border-color: #cbd5e1 !important;
        }
        html.light-mode .bg-slate-800\\/50 {
          background: rgba(226, 232, 240, 0.7) !important;
        }
        html.light-mode .bg-slate-800 {
          background: #e2e8f0 !important;
        }
        html.light-mode .shimmer-text {
          background: linear-gradient(90deg, #db2777, #9333ea, #2563eb, #9333ea, #db2777);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        html.light-mode .static-shimmer-text {
          background: linear-gradient(90deg, #db2777, #9333ea, #2563eb, #9333ea, #db2777);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        html.light-mode .bg-pink-500\\/10,
        html.light-mode .bg-purple-500\\/10,
        html.light-mode .bg-blue-500\\/10 {
          opacity: 0.7;
        }
        /* Light mode gradient backgrounds */
        html.light-mode .from-pink-600\\/20,
        html.light-mode .to-purple-600\\/20 {
          --tw-gradient-from: rgba(219, 39, 119, 0.15);
          --tw-gradient-to: rgba(147, 51, 234, 0.15);
        }
        html.light-mode .from-pink-900\\/10,
        html.light-mode .via-purple-900\\/10,
        html.light-mode .to-blue-900\\/10 {
          background: linear-gradient(to right, rgba(219, 39, 119, 0.08), rgba(147, 51, 234, 0.08), rgba(37, 99, 235, 0.08)) !important;
        }
        html.light-mode .from-blue-900\\/20,
        html.light-mode .to-purple-900\\/20 {
          background: linear-gradient(to right, rgba(37, 99, 235, 0.1), rgba(147, 51, 234, 0.1)) !important;
        }
        html.light-mode .from-yellow-600\\/20,
        html.light-mode .to-orange-600\\/20 {
          background: linear-gradient(to bottom right, rgba(202, 138, 4, 0.15), rgba(234, 88, 12, 0.15)) !important;
        }
        html.light-mode .from-pink-600\\/10,
        html.light-mode .to-purple-600\\/10 {
          background: linear-gradient(to right, rgba(219, 39, 119, 0.08), rgba(147, 51, 234, 0.08)) !important;
        }
        /* Light mode photo placeholder */
        html.light-mode .photo-placeholder {
          background: linear-gradient(135deg, rgba(219, 39, 119, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%);
          border-color: rgba(219, 39, 119, 0.25);
        }
        /* Light mode border glow */
        html.light-mode .border-glow {
          border-color: rgba(219, 39, 119, 0.4) !important;
        }
        /* Light mode status badges */
        html.light-mode .bg-green-500\\/20 {
          background: rgba(34, 197, 94, 0.15) !important;
        }
        html.light-mode .bg-yellow-500\\/20 {
          background: rgba(234, 179, 8, 0.15) !important;
        }
        /* Light mode footer text */
        html.light-mode .border-pink-800\\/30 {
          border-color: rgba(219, 39, 119, 0.2) !important;
        }
        html.light-mode .border-pink-500\\/20 {
          border-color: rgba(219, 39, 119, 0.25) !important;
        }
        html.light-mode .border-blue-500\\/20 {
          border-color: rgba(37, 99, 235, 0.25) !important;
        }
        /* Prevent hero name descenders from being clipped on small screens */
        /* More generous spacing for a range of small screens to avoid clipping */
        @media (max-width: 820px) {
          #hero h1.hero-name {
            padding-bottom: 0.7em !important;
            line-height: 1.18 !important;
            overflow: visible !important;
            margin-bottom: 1.05rem !important;
          }
          /* Keep the gradient span inline-block and add slight bottom padding */
          #hero h1.hero-name .shimmer-text {
            display: inline-block !important;
            padding-bottom: 0.22em !important;
          }
        }
        /* Light mode invert for dark icons */
        html.light-mode .invert {
          filter: invert(0) !important;
        }
        /* Keep white text on specific buttons */
        html.light-mode .keep-white-text {
          color: white !important;
        }
        /* Light mode project cards - Apple liquid glass design */
        html.light-mode .group.bg-slate-900\\/50.backdrop-blur {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(226, 232, 240, 0.6) 50%, rgba(241, 245, 249, 0.7) 100%) !important;
          backdrop-filter: blur(24px) saturate(180%) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.08),
            0 2px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            inset 0 -1px 0 rgba(0, 0, 0, 0.05) !important;
          position: relative !important;
        }
        html.light-mode .group.bg-slate-900\\/50.backdrop-blur::before {
          content: '' !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.02) 100%) !important;
          border-radius: inherit !important;
          pointer-events: none !important;
        }
        html.light-mode .group.bg-slate-900\\/50.backdrop-blur:hover {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.9) 0%, rgba(226, 232, 240, 0.75) 50%, rgba(241, 245, 249, 0.85) 100%) !important;
          backdrop-filter: blur(28px) saturate(200%) !important;
          border-color: rgba(236, 72, 153, 0.4) !important;
          box-shadow: 
            0 16px 48px rgba(0, 0, 0, 0.12),
            0 4px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            inset 0 -1px 0 rgba(0, 0, 0, 0.03),
            0 0 0 1px rgba(236, 72, 153, 0.1) !important;
          transform: translateY(-2px) !important;
        }
        html.light-mode .group.bg-slate-900\\/50.backdrop-blur:hover::before {
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, transparent 40%, transparent 60%, rgba(0, 0, 0, 0.01) 100%) !important;
        }
        /* Light mode quick facts cards - liquid glass effect */
        html.light-mode .bg-slate-900\\/50.backdrop-blur:not(.group) {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.75) 0%, rgba(226, 232, 240, 0.55) 50%, rgba(241, 245, 249, 0.65) 100%) !important;
          backdrop-filter: blur(20px) saturate(160%) !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          box-shadow: 
            0 6px 24px rgba(0, 0, 0, 0.06),
            0 1px 8px rgba(0, 0, 0, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(0, 0, 0, 0.04) !important;
        }
        html.light-mode .bg-slate-900\\/50.backdrop-blur:not(.group):hover {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.85) 0%, rgba(226, 232, 240, 0.7) 50%, rgba(241, 245, 249, 0.8) 100%) !important;
          backdrop-filter: blur(24px) saturate(180%) !important;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.08),
            0 2px 12px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            inset 0 -1px 0 rgba(0, 0, 0, 0.03) !important;
        }
        /* Light mode photo placeholder - liquid glass effect */
        html.light-mode .photo-placeholder {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.7) 0%, rgba(226, 232, 240, 0.5) 50%, rgba(241, 245, 249, 0.6) 100%) !important;
          backdrop-filter: blur(16px) saturate(140%) !important;
          border-color: rgba(236, 72, 153, 0.4) !important;
          box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6),
            inset 0 -1px 0 rgba(0, 0, 0, 0.03) !important;
        }
        /* Light mode value pills/tech badges - liquid glass effect */
        html.light-mode .bg-slate-900\\/80 {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(226, 232, 240, 0.7) 100%) !important;
          backdrop-filter: blur(12px) saturate(120%) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
          box-shadow: 
            0 2px 12px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.7) !important;
        }
        html.light-mode .bg-slate-900\\/80:hover {
          background: linear-gradient(135deg, rgba(241, 245, 249, 0.9) 0%, rgba(226, 232, 240, 0.85) 100%) !important;
          backdrop-filter: blur(16px) saturate(140%) !important;
          box-shadow: 
            0 4px 16px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
        }
        /* Light mode tooltips - subtle glass effect */
        html.light-mode .bg-slate-800 {
          background: rgba(241, 245, 249, 0.95) !important;
          backdrop-filter: blur(8px) !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
        }
        /* Light mode colored text improvements */
        html.light-mode .text-pink-400 {
          color: #be185d !important; /* Darker pink for better contrast */
        }
        html.light-mode .text-purple-400 {
          color: #7c3aed !important; /* Darker purple for better contrast */
        }
        html.light-mode .text-blue-400 {
          color: #1d4ed8 !important; /* Darker blue for better contrast */
        }
        html.light-mode .text-green-400 {
          color: #15803d !important; /* Darker green for better contrast */
        }
        html.light-mode .text-yellow-400 {
          color: #ca8a04 !important; /* Darker yellow for better contrast */
        }
        /* Light mode ripple effects - more visible borders */
        html.light-mode .border-pink-400\\/40 {
          border-color: rgba(236, 72, 153, 0.6) !important;
        }
        html.light-mode .border-purple-400\\/30 {
          border-color: rgba(168, 85, 247, 0.5) !important;
        }
        html.light-mode .border-blue-400\\/20 {
          border-color: rgba(59, 130, 246, 0.4) !important;
        }
      `}</style>

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="fixed pointer-events-none z-0"
          style={{
            left: ripple.x,
            top: ripple.y,
          }}
        >
          {/* First ripple ring */}
          <div 
            className="absolute rounded-full border border-pink-400/40"
            style={{
              width: '150px',
              height: '150px',
              animation: 'ripple 2s ease-out forwards',
            }}
          />
          {/* Second ripple ring - delayed */}
          <div 
            className="absolute rounded-full border border-purple-400/30"
            style={{
              width: '250px',
              height: '250px',
              animation: 'ripple 2s ease-out 0.15s forwards',
              opacity: 0,
            }}
          />
          {/* Third ripple ring - more delayed */}
          <div 
            className="absolute rounded-full border border-blue-400/20"
            style={{
              width: '350px',
              height: '350px',
              animation: 'ripple 2s ease-out 0.3s forwards',
              opacity: 0,
            }}
          />
        </div>
      ))}

      {/* Smooth cursor glow that follows mouse */}
      <div 
        className="fixed pointer-events-none z-0 transition-opacity duration-300"
        style={{
          left: mousePos.x,
          top: mousePos.y,
          transform: 'translate(-50%, -50%)'
        }}
      >
        {/* Inner glow */}
        <div 
          className="absolute rounded-full"
          style={{
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15) 0%, rgba(168, 85, 247, 0.08) 40%, transparent 70%)',
            transform: 'translate(-50%, -50%)'
          }}
        />
        {/* Outer subtle ring */}
        <div 
          className="absolute rounded-full"
          style={{
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 60%)',
            transform: 'translate(-50%, -50%)'
          }}
        />
      </div>

      {/* Ambient background orbs */}
      {/* Ambient background orbs - always cover viewport */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ minWidth: '100vw', minHeight: '100vh', zIndex: 0 }}>
        <div 
          className="absolute w-96 h-96 bg-pink-500/10 rounded-full blur-3xl glow"
          style={{ top: '10%', left: '10%' }}
        />
        <div 
          className="absolute w-80 h-80 bg-purple-500/10 rounded-full blur-3xl glow"
          style={{ top: '50%', right: '10%', animationDelay: '2s' }}
        />
        <div 
          className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl glow"
          style={{ bottom: '10%', left: '30%', animationDelay: '4s' }}
        />
      </div>

      {/* Floating particles with subtle cursor influence */}
      <div className="fixed inset-0 pointer-events-none opacity-40">
        {particlesRef.current.map((particle) => {
          // Calculate subtle offset based on cursor distance
          const centerX = (typeof window !== 'undefined' ? window.innerWidth : 1000) * (particle.baseX / 100);
          const centerY = (typeof window !== 'undefined' ? window.innerHeight : 800) * (particle.baseY / 100);
          const dx = mousePos.x - centerX;
          const dy = mousePos.y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxInfluence = 150; // Max distance for influence
          const influence = Math.max(0, 1 - distance / maxInfluence) * 8; // Max 8px movement
          const offsetX = distance > 0 ? (dx / distance) * influence : 0;
          const offsetY = distance > 0 ? (dy / distance) * influence : 0;
          
          return (
            <div
              key={particle.id}
              className="absolute w-1.5 h-1.5 rounded-full"
              style={{
                left: `${particle.baseX}%`,
                top: `${particle.baseY}%`,
                background: particle.color,
                boxShadow: `0 0 6px ${particle.color}`,
                transform: `translate(${offsetX}px, ${offsetY}px)`,
                transition: 'transform 0.3s ease-out',
                animation: `float ${particle.duration}s ease-in-out infinite`,
                animationDelay: `${particle.delay}s`
              }}
            />
          );
        })}
      </div>

      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center relative px-4 sm:px-6 py-16 sm:py-20 overflow-visible">
        <div className="max-w-5xl w-full mx-auto">
          {/* Location tag */}
          <div className={`mb-6 opacity-0 ${heroTextVisible ? 'slide-up stagger-1' : ''}`}>
            <span className="text-pink-400/80 text-sm font-mono tracking-widest">{'// Cavite, Philippines'}</span>
          </div>
          
          {/* Main name with gradient */}
          <h1
            className={`hero-name text-6xl xs:text-7xl sm:text-8xl md:text-8xl font-bold mb-6 opacity-0 ${heroTextVisible ? 'slide-up stagger-2' : ''}`}
            style={{ lineHeight: 1.1, paddingBottom: '0.2em', overflow: 'visible', marginBottom: '1rem' }}
          >
            <span className="shimmer-text" style={{ lineHeight: 1.1, display: 'inline-block' }}>
              Caryl Joy
            </span>
            <br />
            <span className="text-slate-200" style={{ lineHeight: 1.1 }}>Cabrera</span>
          </h1>
          
          {/* Typing effect tagline */}
          <div className={`text-lg xs:text-xl sm:text-2xl md:text-3xl font-light text-slate-400 mb-4 h-10 opacity-0 ${heroTextVisible ? 'slide-up stagger-3' : ''}`}> 
            <span className="cursor-blink">{typedText}</span>
          </div>

          {/* Description with personality */}
          <div className={`text-base sm:text-lg md:text-xl text-slate-400 mb-8 max-w-2xl leading-relaxed opacity-0 ${heroTextVisible ? 'slide-up stagger-4' : ''}`}> 
            IT cybersecurity professional applying technical precision with thoughtful execution.
          </div>

          {/* Value pills */}
          <div className={`flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10 opacity-0 ${heroTextVisible ? 'slide-up stagger-5' : ''}`}> 
            {values.map((v, i) => (
              <div key={i} className="group relative">
                <span className="px-4 py-2 bg-slate-900/80 border border-slate-800 hover:border-pink-500/50 rounded-full text-sm text-slate-300 transition-all duration-300 cursor-default">
                  {v.word}
                </span>
                <div className="absolute bottom-full left-0 mb-2 px-3 py-1 bg-slate-800 rounded text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {v.description}
                </div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className={`w-full opacity-0 mb-6 sm:mb-8 ${heroTextVisible ? 'slide-up' : ''}`} style={{ animationDelay: '0.6s' }}>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-2 sm:mb-0 w-full">
              <a href="mailto:caryldcabrera@gmail.com" 
                 className="group px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-lg transition-all duration-300 font-medium flex items-center gap-2 shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 w-full sm:w-auto keep-white-text">
                <Mail size={18} />
                Let's Connect
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <div className="flex flex-row gap-2 w-full sm:w-auto">
                <a href="https://linkedin.com/in/caryljoycabrera" 
                   target="_blank"
                   rel="noopener noreferrer"
                   className="px-6 py-3 border border-slate-700 hover:border-pink-500 rounded-lg transition-all duration-300 font-medium flex items-center gap-2 hover:bg-pink-500/5 w-1/2 sm:w-auto">
                  <Linkedin size={18} />
                  LinkedIn
                </a>
                <a href="https://github.com/caryljoycabrera" 
                   target="_blank"
                   rel="noopener noreferrer"
                   className="px-6 py-3 border border-slate-700 hover:border-purple-500 rounded-lg transition-all duration-300 font-medium flex items-center gap-2 hover:bg-purple-500/5 w-1/2 sm:w-auto">
                  <Github size={18} />
                  GitHub
                </a>
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className={`flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500 opacity-0 ${heroTextVisible ? 'fade-in' : ''}`} style={{ animationDelay: '0.8s' }}>
            <div className="flex items-center gap-2 hover:text-pink-400 transition-colors">
              <Sparkles size={16} className="text-pink-400" />
              <span>SOC Specialist</span>
            </div>
            <div className="flex items-center gap-2 hover:text-purple-400 transition-colors">
              <Heart size={16} className="text-purple-400" />
              <span>Scholar</span>
            </div>
            <div className="flex items-center gap-2 hover:text-blue-400 transition-colors">
              <Trophy size={16} className="text-blue-400" />
              <span>Student Leader</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 sm:bottom-10 left-1/2 -translate-x-1/2">
          <ChevronDown className="text-pink-400/60 animate-bounce" size={32} />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('about')}`}> 
          <h2 className="text-4xl font-bold mb-12 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">01.</span>
            <span>Who I Am</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
            <div className="flex justify-center items-center mb-6 md:mb-0 md:col-span-1">
              <div className="w-56 h-72 xs:w-64 xs:h-80 sm:w-80 sm:h-96 md:w-96 md:h-[28rem] rounded-2xl photo-placeholder flex items-center justify-center border-2 border-dashed border-pink-400/30 bg-slate-900/40 relative overflow-hidden mx-auto">
                <img 
                  src="/images/photo.png" 
                  alt="Caryl Joy Cabrera" 
                  className="w-full h-full object-cover max-w-[350px] max-h-[500px] xs:max-w-[400px] xs:max-h-[540px] sm:max-w-[480px] sm:max-h-[600px] md:max-w-[540px] md:max-h-[700px] mx-auto" 
                  style={{ aspectRatio: '3/4' }}
                />
              </div>
            </div>
            {/* About content (center/right) */}
            <div className="md:col-span-2 space-y-4 text-slate-300 leading-relaxed">
              <p className="text-lg">
                I started my journey writing code, and I found my strength in orchestrating the <span className="text-pink-400">systems</span> that make businesses protected and reliable. Currently, I am refining this craft as an <span className="text-pink-400">analyst at Anchored Solutions</span>.
              </p>
              <p>
                I drive initiatives that generate tangible efficiency, <span className="text-purple-400 font-semibold">ensuring that true results are built on a foundation of security and discipline.</span> I led student groups that delivered measurable success. I balanced freelance work with academics and extra-curricular activities while being a <span className="text-purple-400 font-semibold">student assistant</span> and <span className="text-purple-400 font-semibold">class representative</span>. I wore multiple hats, and I thrived.
              </p>
              <p>
                Beyond standard IT support, I bring <span className="text-blue-400 font-semibold">security operations expertise</span> and <span className="text-blue-400 font-semibold">proactive threat monitoring</span> to the table. Whether triaging security alerts, automating incident response workflows, or managing endpoint vulnerabilities, I prioritize defending assets and mitigating risk. I don't just resolve issues; I cultivate <span className="text-blue-400 font-semibold">zero-trust environments</span> and <span className="text-blue-400 font-semibold">operational resilience</span>.
              </p>
              {/* Quick facts grid (below about content) */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6">
                <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-4 hover:border-pink-500/30 transition-all">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">Current Status</span>
                  <p className="text-blue-400 font-medium">IT Security and Operations Analyst</p>
                </div>
                <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-4 hover:border-purple-500/30 transition-all">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">English Level</span>
                  <p className="text-slate-200 font-medium">C1 (TOEIC)</p>
                </div>
                <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-4 hover:border-blue-500/30 transition-all">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">Previous Experience</span>
                  <p className="text-slate-200 font-medium">IT Compliance Intern</p>
                </div>
                <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-4 hover:border-blue-500/30 transition-all">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">Education</span>
                  <p className="text-slate-200 font-medium">BSIT - Web Dev (August 2026)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work Experience Section */}
      <section id="experience" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('experience')}`}> 
          <h2 className="text-4xl font-bold mb-12 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">02.</span>
            <span>Experience</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>

          {/* Timeline style experience */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-pink-500 via-purple-500 to-blue-500 hidden md:block" />
            
            <div className="space-y-6 sm:space-y-8">
              {workExperience.map((exp, idx) => (
                <div key={idx} className="relative md:pl-20">
                  {/* Timeline dot */}
                  <div className="absolute left-6 w-4 h-4 bg-pink-500 rounded-full border-4 border-slate-950 hidden md:block" style={{ top: '28px' }} />
                  
                  <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-6 hover:border-purple-500/30 transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                      <div>
                        <h3 className="text-xl font-semibold text-slate-100">{exp.role}</h3>
                        <p className="text-purple-400">{exp.company}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-slate-800/50 text-slate-400 rounded-full text-xs">{exp.type}</span>
                        <span className="text-pink-400 font-mono text-sm whitespace-nowrap">{exp.period}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 leading-relaxed">{exp.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* More experiences teaser */}
            <div className="mt-10 text-center">
              <p className="text-slate-500 mb-4">Focused on IT Project Management and Security Operations!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('certifications')}`}> 
          <h2 className="text-4xl font-bold mb-12 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">03.</span>
            <span>Certifications</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {certifications.map((cert, idx) => (
              <div key={idx} className="group bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-6 hover:border-blue-500/30 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{cert.icon}</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="text-lg font-semibold text-slate-100 group-hover:text-blue-400 transition-colors">{cert.title}</h3>
                      <span className="text-blue-400 font-mono text-sm">{cert.year}</span>
                    </div>
                    <p className="text-slate-400 text-sm">{cert.org}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* More certifications teaser */}
          <div className="mt-10 text-center">
            <p className="text-slate-500 mb-4">New certifications to be added soon...</p>
          </div>
        </div>
      </section>

      {/* Tech Stack Section with Logos */}
      <section id="skills" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('skills')}`}> 
          <h2 className="text-4xl font-bold mb-4 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">04.</span>
            <span>What I Work With</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>
          <p className="text-slate-400 mb-12 max-w-2xl">
            A diverse kit of skills, tools, and technologies that I leverage to bring ideas to life.
          </p>
          
          {/* Category breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="p-6 bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl hover:border-pink-500/30 transition-all">
              <h3 className="text-pink-400 font-semibold mb-3 flex items-center gap-2">
                <Code size={18} />
                Technical Philosophy
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Security-First Design', 'Agile & SDLC', 'Maintainability', 'User-Centricity', 'Data Integrity'].map(item => (
                  <span key={item} className="px-3 py-1 bg-slate-800/50 text-slate-300 rounded-full text-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="p-6 bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl hover:border-purple-500/30 transition-all">
              <h3 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
                <Sparkles size={18} />
                Management Competencies
              </h3>
              <div className="flex flex-wrap gap-2">
                {['IT SecOps and GRC', 'Project Management', 'Technical & Policy Documentation', 'Stakeholder Management', 'Cross-Functional Leadership', 'Operational Efficiency', 'Events, Logistics, and Marketing'].map(item => (
                  <span key={item} className="px-3 py-1 bg-slate-800/50 text-slate-300 rounded-full text-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <br></br>

          {/* Tech icons grid */}
          <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3 sm:gap-4 mb-8 sm:mb-12">
            {techStack.map((tech, idx) => (
              <div 
                key={idx} 
                className="tech-icon group relative flex flex-col items-center gap-2 p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-blue-500/30 cursor-default"
              >
                <img 
                  src={tech.icon} 
                  alt={tech.name}
                  className={`w-10 h-10 ${tech.invert ? 'invert' : ''}`}
                />
                <span className="text-xs text-slate-400 group-hover:text-slate-200 transition-colors text-center">
                  {tech.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('education')}`}> 
          <h2 className="text-4xl font-bold mb-12 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">05.</span>
            <span>Education</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>

          <div className="bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-8 hover:border-purple-500/30 transition-all duration-300">
            <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-6">
              <div className="p-4 bg-gradient-to-br from-pink-600/20 to-purple-600/20 rounded-xl border-glow">
                <GraduationCap className="text-pink-400" size={40} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-slate-100 mb-2">{education.degree}</h3>
                <p className="text-lg text-purple-400 mb-1">with Specialization in {education.specialization}</p>
                <p className="text-slate-400">{education.university}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-2xl font-bold text-pink-400">GPA: {education.gpa}</p>
                <p className="text-slate-500 text-sm">Graduated {education.graduation}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wider">Honors & Scholarships</h4>
              <div className="flex flex-wrap gap-2">
                {education.honors.map((honor, idx) => (
                  <span key={idx} className="px-4 py-2 bg-gradient-to-r from-pink-600/10 to-purple-600/10 text-slate-300 rounded-lg text-sm border border-pink-500/20 hover:border-pink-500/40 transition-colors">
                    <Star className="inline w-3 h-3 mr-1.5 text-yellow-400" />
                    {honor}
                  </span>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-xl border border-blue-500/20">
              <p className="text-slate-300 leading-relaxed">
                <span className="text-blue-400 font-semibold">Key Achievement: </span>
                {education.achievement}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('projects')}`}> 
          <h2 className="text-4xl font-bold mb-4 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">06.</span>
            <span>Projects</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>
          <p className="text-slate-400 mb-12 max-w-2xl">
            A showcase of my multifaceted journey through innovation across various domains.
          </p>

          <div className="flex space-x-4 mb-8">
            {['Technical Solutions', 'Community Leadership', 'Creative Outputs'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded transition-colors ${
                  activeTab === tab 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.filter(project => project.category === activeTab).slice(0, 6).map((project, idx) => (
              <div 
                key={project.image || idx} 
                className="group bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl overflow-hidden hover:border-pink-500/30 transition-all duration-500 hover:-translate-y-2"
              >
                {/* Project image */}
                <div className={`photo-placeholder aspect-video flex items-center justify-center group-hover:border-pink-500/50 transition-all relative ${project.image ? 'cursor-pointer' : ''}`} onClick={project.image ? () => { setSelectedImage(project.image); setIsModalOpen(true); } : undefined}>
                  {project.image ? (
                    <>
                      <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                      {/* Desktop hover overlay */}
                      <div className="hidden md:flex absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 items-center justify-center">
                        <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      {/* Mobile always-visible zoom icon */}
                      <div className="md:hidden absolute bottom-2 right-2 bg-black/60 border border-white/30 rounded-full p-1">
                        <ZoomIn size={20} className="text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-500">
                      <Sparkles size={32} className="group-hover:text-pink-400 transition-colors" />
                    </div>
                  )}
                </div>
                
                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">{project.type}</span>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs text-slate-400 font-mono">{project.year}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-100 mb-2 group-hover:text-pink-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tech.map((t, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-slate-900/80 border border-slate-800 hover:border-pink-500/50 text-slate-400 rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex gap-3">
                    {project.link && (
                      <a href={project.link} target="_blank" rel="noopener noreferrer" 
                         className="text-sm text-pink-400 hover:text-pink-300 flex items-center gap-1">
                        <ExternalLink size={14} /> View
                      </a>
                    )}
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noopener noreferrer"
                         className="text-sm text-slate-400 hover:text-slate-300 flex items-center gap-1">
                        <Github size={14} /> Code
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Projects button */}
          <div className="mt-10 text-center">
            <Link to="/projects" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-lg transition-all duration-300 font-medium shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 keep-white-text">
              View All Projects
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section id="awards" className="py-16 sm:py-24 px-4 sm:px-6 relative">
        <div className={`max-w-5xl mx-auto ${sectionClass('awards')}`}> 
          <h2 className="text-4xl font-bold mb-4 flex items-center gap-3">
            <span className="text-pink-400 font-mono text-lg">07.</span>
            <span>Awards and Recognition</span>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-800 to-transparent ml-4" />
          </h2>
          <p className="text-slate-400 mb-12 max-w-2xl">
            Leadership isn't a title, it's a responsibility. These moments reminded me why I do what I do.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {awards.map((award, idx) => (
              <div 
                key={idx}
                className="group relative bg-slate-900/50 backdrop-blur border border-slate-800 rounded-xl p-6 hover:border-yellow-500/30 transition-all duration-300 overflow-hidden"
              >
                {/* Subtle gold accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-500/10 to-transparent rounded-bl-full" />
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-xl">
                    <Trophy className="text-yellow-400" size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="text-lg font-semibold text-slate-100 group-hover:text-yellow-400 transition-colors">
                        {award.title}
                      </h3>
                      <span className="text-yellow-400 font-mono text-sm">{award.year}</span>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">{award.event}</p>
                    <p className="text-slate-500 text-sm">{award.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Moments to Remember */}
          <div className="mt-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[1,2,3,4].map((num) => (
                <div key={num} className="aspect-[3/4] bg-slate-900/50 border-2 border-dashed border-pink-400/30 rounded-2xl flex items-center justify-center relative overflow-hidden">
                  <img src={`/images/moment${num}.jpg`} alt={`Moment ${num}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
          {/* Leadership highlight */}
          <div className="mt-8 sm:mt-12 p-4 sm:p-6 bg-gradient-to-r from-pink-900/10 via-purple-900/10 to-blue-900/10 border border-pink-800/30 rounded-xl">
            <h3 className="text-lg font-semibold mb-6 text-pink-400 flex items-center gap-2">
              <Zap size={20} />
              Student Leadership Experience Highlights (2021 - 2026)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: CSG and CSO */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-slate-200 font-semibold mb-2">College Student Government</h4>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Vice Governor
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Legislative Councilor
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Logistics Committee
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-slate-200 font-semibold mb-2">Council of Student Organizations</h4>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Associate Executive Director
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Director for Spiritual Activities
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-pink-400">•</span>
                      Internal Affairs Executive Committee
                    </li>
                  </ul>
                </div>
              </div>
              {/* Column 2: COSA, SINAG, TIPH */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-slate-200 font-semibold mb-2">Circle of Student Assistants</h4>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Director for External Affairs
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Junior Officer for Business Manager
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Sponsorship and Partnership Committee
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-slate-200 font-semibold mb-2">SINAG Society of Leaders</h4>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Junior Officer for Communications
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-slate-200 font-semibold mb-2">The Initiative PH</h4>
                  <ul className="space-y-1 text-slate-300 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Department of Mental Health
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-purple-400">•</span>
                      Multimedia Artist
                    </li>
                  </ul>
                </div>
              </div>
              {/* Column 3: Key Achievements */}
              <div>
                <h4 className="text-slate-200 font-semibold mb-4">Key Achievements</h4>
                <ul className="space-y-3 text-slate-300 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Spearheaded events, programs, and legislations
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Secured partnerships and sponsorships
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Organized workshops and training sessions
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Took part in communications, marketing, and logistics
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Contributed to initiatives and advocacy campaigns
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    Coordinated with cross-functional teams
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    
      {/* Contact / Footer Section */}
      <footer id="contact" className="py-16 sm:py-24 px-4 sm:px-6 border-t border-slate-900 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Personal closing statement */}
          <div className="mb-12">
            <h2 className="text-3xl xs:text-4xl md:text-5xl font-bold mb-6">
        <span className="shimmer-text arsenica-antiqua">Let's Drive Meaningful Impact</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
              I am focused on building a long-term career defined by <span className="text-pink-400">principled governance</span>, 
              <span className="text-purple-400"> operational integrity</span>, and a <span className="text-blue-400">commitment to security</span>.
              I am ready to further my skills and experience.
            </p>
          </div>
          
          {/* Contact buttons */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
            <a href="mailto:caryldcabrera@gmail.com" 
               className="group px-8 py-4 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-xl transition-all duration-300 font-medium text-lg shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 flex items-center gap-2 keep-white-text">
              <Mail size={20} />
              caryldcabrera@gmail.com
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Social links */}
          <div className="flex justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
            <a href="https://linkedin.com/in/caryljoycabrera" 
               target="_blank" 
               rel="noopener noreferrer"
               className="p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-pink-500/50 hover:bg-pink-500/5 transition-all group">
              <Linkedin size={24} className="text-slate-400 group-hover:text-pink-400 transition-colors" />
            </a>
            <a href="https://github.com/caryljoycabrera" 
               target="_blank" 
               rel="noopener noreferrer"
               className="p-4 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-purple-500/50 hover:bg-purple-500/5 transition-all group">
              <Github size={24} className="text-slate-400 group-hover:text-purple-400 transition-colors" />
            </a>
          </div>

          {/* Footer note */}
          <div className="pt-6 sm:pt-8 border-t border-slate-900">
            <p className="text-slate-600 text-sm mb-2">
              Designed with the heart. Built with React.
            </p>
            <p className="text-slate-700 text-sm">
              © {new Date().getFullYear()} Caryl Joy Cabrera
            </p>
          </div>
        </div>
      </footer>

      {/* Full-screen image modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-start justify-center p-4" style={{ paddingTop: modalTopOffset + 'px', touchAction: 'none' }} onClick={() => setIsModalOpen(false)} onWheel={() => setIsModalOpen(false)} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchStart={(e) => { if (zoom <= 1) e.preventDefault(); }}>
          <div className="relative max-w-full max-h-[calc(100vh-96px)] w-full flex items-center justify-center">
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                ref={closeBtnRef}
                className="absolute right-3 top-3 text-white hover:text-pink-400 transition-colors z-10 bg-black/40 rounded-full p-1"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close image"
              >
                <X size={28} />
              </button>
              <button
                className="absolute left-3 top-3 text-white hover:text-pink-400 transition-colors z-10 bg-black/40 rounded-full p-1"
                onClick={handleZoomIn}
                aria-label="Zoom in"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <button
                className="absolute left-3 top-16 text-white hover:text-pink-400 transition-colors z-10 bg-black/40 rounded-full p-1"
                onClick={handleZoomOut}
                aria-label="Zoom out"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <img
                ref={imgRef}
                src={selectedImage}
                alt="Full screen"
                className={`max-w-full max-h-[calc(100vh-128px)] object-contain rounded ${zoom > 1 ? 'cursor-move' : ''}`}
                style={{ transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`, transformOrigin: 'center' }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onTouchStart={(e) => { handleTouchStart(e); if (zoom > 1) e.preventDefault(); }}
                onTouchMove={(e) => { handleTouchMove(e); if (zoom > 1) e.preventDefault(); }}
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}

      </div>
    </>
  );
}